import { type FC, useCallback, useRef, useState } from "react";
import { HStack, Popover, TriggerButton, TriggerButtonIntent } from "widgets";
import { useOverlay } from "widgets/hooks";

import { type Attribute } from "@/graphql.ts";
import type { IAttributeWithDefault } from "@/ui-modules/admin/AddSampleTypeDialog.js";

import { SelectAttributeForm } from "./SelectAttributeForm.js";

interface ISelectAttributesManagerControlsProps {
  availableAttributes: Attribute[];
  parentContext?: boolean;
  onAddAttributes: (attributes: Attribute[]) => void;
  handleRemoveSelection: () => void;
  refreshAvailableAttributes: () => Promise<IAttributeWithDefault[]>;
  getRemoveAttributesDisabledTooltip: () => string;
  getAddAttributesDisabledTooltip: () => string;
}

export const SelectAttributesManagerControls: FC<ISelectAttributesManagerControlsProps> = ({
  refreshAvailableAttributes,
  getRemoveAttributesDisabledTooltip,
  getAddAttributesDisabledTooltip,
  availableAttributes,
  onAddAttributes,
  parentContext,
  handleRemoveSelection,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const refreshAttributes = useCallback(() => {
    async function load() {
      setIsLoading(true);
      await refreshAvailableAttributes();
      setIsLoading(false);
    }

    void load();
  }, [refreshAvailableAttributes]);

  const { overlayProps, state, triggerProps } = useOverlay({
    onOpenChange(isOpen) {
      if (isOpen) {
        refreshAttributes();
      }
    },
  });

  const removeButtonDisabledTooltip: string = getRemoveAttributesDisabledTooltip();
  const addButtonDisabledTooltip: string = getAddAttributesDisabledTooltip();

  return (
    <HStack>
      <TriggerButton
        icon={"add"}
        label={"Add"}
        showLabel={true}
        ref={triggerRef}
        intent={TriggerButtonIntent.Secondary}
        isDisabled={!!addButtonDisabledTooltip}
        tooltip={addButtonDisabledTooltip || "Add a new attribute to the list"}
        {...triggerProps}
      />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef} shouldCloseOnInteractOutside={() => false}>
        <SelectAttributeForm
          availableAttributes={availableAttributes}
          onAddAttributes={onAddAttributes}
          onClose={() => {
            state.close();
          }}
          parentContext={parentContext}
          isLoading={isLoading}
        />
      </Popover>

      <TriggerButton
        icon={"remove"}
        label={"Remove"}
        showLabel={true}
        intent={TriggerButtonIntent.Secondary}
        isDisabled={!!removeButtonDisabledTooltip}
        tooltip={removeButtonDisabledTooltip || "Remove the selection from the list"}
        onPress={handleRemoveSelection}
      />
    </HStack>
  );
};
